import { styled } from "@mui/material/styles";
import DojoView from "csi/page/DojoView";
import { ConfirmDialogProps } from "designSystem/components/dialog/ConfirmDialog";
import { MessageDialogProps } from "designSystem/components/dialog/MessageDialog";
import { useLocalReactionStore } from "designSystem/hooks";
import { Box, ConfirmDialog, FlexColumn, FlexRow, FlexSideDrawer, FormControlLabel, FullScreenDialog, MessageDialog, PopupDialog, Switch, Tab, TabPanel, Tabs, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { grey } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FC, FunctionComponent } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import css from "./CustomViewDialog.module.css";
import CustomViewDialogHeader from "./CustomViewDialogHeader";
import CustomViewDialogPartial from "./CustomViewDialogPartial";
import CustomViewDialogStore, { CustomViewDialogStoreContext, useCustomViewDialogStore } from "./CustomViewDialogStore";
import { ErrorFallback } from "./ErrorFallback";
import CustomViewDialogLeftPanel from "./customViewLeftPanel/CustomViewDialogLeftPanel";
import CustomViewDialogHeaderForSingleCustomView from "./CustomViewDialogHeaderForSingleCustomView";

export interface CustomViewDialogProps {
	/** handle the Cancel action */
	onCancel: VoidFunction;

	/** handle the Save&Close action */
	onSaveAndClose: () => Promise<void>;

	/** store for custom view dialog */
	store: CustomViewDialogStore;
}

const StyledTab = styled(Tab)({
	height: "35px",
});

const StyledTabs = styled(Tabs)({
	padding: "0 24px",
});

const StyledConfirmDialog: FC<ConfirmDialogProps> = observer((props) => {
	const {
		zIndexForPopup,
	} = useCustomViewDialogStore();

	return (
		<ConfirmDialog {...props} zIndex={zIndexForPopup} />
	);
});

const StyledMessageDialog: FC<MessageDialogProps> = observer((props) => {
	const {
		zIndexForPopup,
	} = useCustomViewDialogStore();

	return (
		<MessageDialog {...props} zIndex={zIndexForPopup} />
	);
});

const CustomViewDialogTabs: FunctionComponent = observer(() => {
	const loc = useLocale();
	const { showBoundary } = useErrorBoundary();
	const {
		availableTabs,
		error,
		failDeletingError,
		onChangeTab,
		resetDeletingErrorMessage,
		selectedTab,
	} = useCustomViewDialogStore();

	if (error) {
		showBoundary(error);
	}

	return (
		<>
			<StyledTabs
				onChange={onChangeTab}
				value={selectedTab}
			>
				{availableTabs.map(({ id }) => (
					<StyledTab
						data-csi-automation="CustomViewDialogTab"
						key={id}
						label={loc.resource(id)}
						value={id}
					/>
				))}
			</StyledTabs>
			{availableTabs.map(({ Component, id, visited }) => (
				<TabPanel
					flexGrow={1}
					index={id}
					key={id}
					overflow="auto"
					selectedTabIndex={selectedTab}
				>
					{visited && (
						<Component />
					)}
				</TabPanel>
			))}
			{failDeletingError && (
				<StyledMessageDialog
					category="error"
					messageRes="CustomViewRunTimeError"
					messageResParams={[failDeletingError]}
					onClose={resetDeletingErrorMessage}
					open
					titleRes="ERROR"
				/>
			)}
		</>
	);
});

const CustomViewPreview: FunctionComponent = observer(function CustomViewPreview() {
	const {
		allowSuspendPreview,
		previewProps,
		setShowDataInPreview,
		showDataInPreview,
	} = useCustomViewDialogStore();

	return (
		<FlexColumn
			data-csi-automation="CustomViewPreview"
			gap={3}
		>
			{previewProps && allowSuspendPreview && (
				<FlexRow>
					<FormControlLabel
						block
						control={(
							<Switch
								checked={showDataInPreview}
								onChange={setShowDataInPreview}
							/>
						)}
						labelPlacement="start"
						res="CustomViewPreviewLiveData"
					/>
				</FlexRow>
			)}
			{previewProps ? (
				<Box growNoOverflow>
					<DojoView
						{...previewProps}
						forceReadOnly
						fullHeight
					/>
				</Box>
			) : (
				<Text
					res={previewProps ? "CustomViewPreview" : "CustomViewNoPreview"}
					semiBold
				/>
			)}
		</FlexColumn>
	);
});

const CustomViewDialogMain: FC<CustomViewDialogProps> = (props) => {
	const { manageSingleCustomView, resetError } = useCustomViewDialogStore();

	return (
		<ErrorBoundary FallbackComponent={ErrorFallback} onReset={resetError}>
			{manageSingleCustomView ? (
				<CustomViewDialogHeaderForSingleCustomView {...props} />
			) : (
				<CustomViewDialogHeader {...props} />
			)}
			<FlexRow
				data-csi-automation="CustomViewDialogMain"
				growNoOverflow
			>
				<FlexColumn
					data-csi-automation="CustomViewDialogTabs"
					maxWidth={1000}
					minWidth={550}
					width="50%"
				>
					<CustomViewDialogTabs />
				</FlexColumn>
				<FlexColumn
					bgcolor={grey[100]}
					className={css.preview}
					flexGrow={1}
					px={6}
					py={3}
					width="50%"
				>
					<CustomViewPreview />
				</FlexColumn>
			</FlexRow>
		</ErrorBoundary>
	);
};

const CustomViewDialog: FunctionComponent<CustomViewDialogProps> = observer((props) => {
	const { onCancel, store } = props;
	useLocalReactionStore(() => store);
	const {
		manageSingleCustomView,
		confirmValidationError,
		confirmViewDoesNotExist,
		hasPersonalAccess,
		leftPanelOpen,
		onCancelNavigatingToNewCV,
		onConfirmCancelDuplicate,
		onConfirmNavigatingToNewCV,
		onConfirmSaveDuplicate,
		openConfirmationDuplicationDialog,
		openConfirmationSelectionDialog,
		openViewDoesNotExistWarningDialog,
		openViewValidationErrorDialog,
		selectedCustomViewName,
		setLeftPanelOpen,
		settingStore: {
			nameErrorRes,
		},
		zIndex,
	} = store;

	return (
		<CustomViewDialogStoreContext.Provider value={store}>
			{hasPersonalAccess ? (
				<FullScreenDialog
					data-csi-automation="CustomViewDialog"
					open
					sx={{ zIndex }}
				>
					{manageSingleCustomView ? (
						<CustomViewDialogMain {...props} />
					) : (
						<FlexSideDrawer
							drawerNode={<CustomViewDialogLeftPanel />}
							onToggleOpen={setLeftPanelOpen}
							open={leftPanelOpen}
							widthOpen={296}
						>
							<CustomViewDialogMain {...props} />
						</FlexSideDrawer>
					)}
					{openConfirmationSelectionDialog && (
						<StyledConfirmDialog
							category="warn"
							confirmRes="BUTTON_SAVE"
							messageRes="CustomViewDialogLeavePage"
							messageResParams={[selectedCustomViewName]}
							onCancel={onCancelNavigatingToNewCV}
							onConfirm={onConfirmNavigatingToNewCV}
							open
							secondaryButtonRes="BUTTON_DISCARD"
							titleRes="CustomViewDialogLeavePageTitle"
						/>
					)}
					{openConfirmationDuplicationDialog && (
						<StyledConfirmDialog
							category="warn"
							confirmRes="BUTTON_SAVE"
							messageRes="CustomViewDialogLeavePage"
							messageResParams={[selectedCustomViewName]}
							onCancel={onConfirmCancelDuplicate}
							onConfirm={onConfirmSaveDuplicate}
							open
							secondaryButtonRes="BUTTON_DISCARD"
							titleRes="CustomViewDialogLeavePageTitle"
						/>
					)}
					{openViewDoesNotExistWarningDialog && (
						<StyledMessageDialog
							category="warn"
							messageRes="Location_ErrorNode"
							onClose={confirmViewDoesNotExist}
							open
						/>
					)}
					{openViewValidationErrorDialog && (
						<StyledMessageDialog
							category="error"
							messageRes={nameErrorRes}
							onClose={confirmValidationError}
							open
						/>
					)}
				</FullScreenDialog>
			) : (
				<PopupDialog
					minWidth={500}
					onClose={onCancel}
					open
					sx={{ zIndex }}
					titleRes="CustomViewManage"
				>
					{{ main: <CustomViewDialogPartial /> }}
				</PopupDialog>
			)}
		</CustomViewDialogStoreContext.Provider>
	);
});

export default CustomViewDialog;